import { useState } from 'react';
import { Disclosure, Transition } from '@headlessui/react';
import { ChevronRightIcon, LockClosedIcon, SparklesIcon } from '@heroicons/react/24/outline';
import clsx from 'clsx';
import type { LockedNavEntry } from './navConfig';
import { groupLockedByTier } from './navConfig';
import UpgradePrompt from '../UpgradePrompt';

/**
 * The single "Unlock more" upsell surface (UX epic PR 7). Replaces the scattered
 * inline padlocks: every module/feature the bank's tier doesn't include — whole
 * gated sections AND individual gated items — is consolidated here in one
 * collapsed group pinned at the bottom of the nav. Each row opens the existing
 * UpgradePrompt for that feature (the moat pitch, made intentional).
 */
export default function SidebarUpgradeGroup({ locked }: { locked: LockedNavEntry[] }) {
  const [feature, setFeature] = useState<string | null>(null);

  if (locked.length === 0) return null;

  const buckets = groupLockedByTier(locked);

  return (
    <div className="mt-3 border-t border-rule pt-3">
      <Disclosure>
        {({ open }) => (
          <div>
            <Disclosure.Button className="group flex w-full items-center gap-x-3 rounded-md px-3 py-2 text-left text-sm font-semibold text-ink-3 transition-colors hover:bg-panel-2 hover:text-ink">
              <SparklesIcon className="h-5 w-5 shrink-0 text-ink-4 transition-colors group-hover:text-teal-600" />
              <span className="flex-1 truncate">Unlock more</span>
              <span className="rounded-sm bg-panel-2 px-1.5 font-mono text-[0.65rem] text-ink-4">
                {locked.length}
              </span>
              <ChevronRightIcon
                className={clsx(
                  'h-4 w-4 shrink-0 text-ink-4 transition-transform duration-150',
                  open && 'rotate-90'
                )}
              />
            </Disclosure.Button>
            <Transition
              enter="transition duration-100 ease-out"
              enterFrom="transform opacity-0 -translate-y-1"
              enterTo="transform opacity-100 translate-y-0"
              leave="transition duration-75 ease-in"
              leaveFrom="transform opacity-100 translate-y-0"
              leaveTo="transform opacity-0 -translate-y-1"
            >
              <Disclosure.Panel className="mt-1 space-y-2 pl-4">
                {buckets.map((bucket) => (
                  <div key={bucket.tier} className="space-y-0.5">
                    <div className="px-3 pt-1 font-mono text-[0.6rem] uppercase tracking-wider text-ink-4">
                      Upgrade to {bucket.tier}
                    </div>
                    {bucket.entries.map((entry) => (
                      <button
                        key={entry.key}
                        type="button"
                        onClick={() => setFeature(entry.feature)}
                        className="group flex w-full items-center gap-x-3 rounded-md px-3 py-2 text-sm font-medium text-ink-4 transition-colors hover:bg-panel-2 hover:text-ink-3"
                      >
                        <entry.icon className="h-5 w-5 shrink-0 text-ink-4 transition-colors group-hover:text-ink-3" />
                        <span className="truncate">{entry.name}</span>
                        <LockClosedIcon className="ml-auto h-3.5 w-3.5 shrink-0 text-ink-4" />
                      </button>
                    ))}
                  </div>
                ))}
              </Disclosure.Panel>
            </Transition>
          </div>
        )}
      </Disclosure>
      <UpgradePrompt
        isOpen={feature !== null}
        onClose={() => setFeature(null)}
        feature={feature ?? undefined}
      />
    </div>
  );
}
